import * as React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withStyles } from '@material-ui/core/styles';
import { TableHead, Table, TableCell, TableRow, TableBody, Typography } from '@material-ui/core';
import { ReportSectionKeys } from '../+store/aToF';
import { numberStringOrDash } from '../D3/Common/utils';

const isDemo = process.env.REACT_APP_THEME === 'demo';

const styles = {
  root: {
    marginBottom: '200px',
    background: isDemo
      ? '#F9FBFC'
      : 'linear-gradient(180deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%), rgba(134, 240, 255, 0.65)',
  },
  componentRow: {
    height: '24px',
    // background: 'linear-gradient(270deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%), rgba(61, 232, 255, 0.65)',
  },
  subComponentRow: {
    height: '24px',
    // background: 'linear-gradient(270deg, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%), rgba(134, 240, 255, 0.65)',
  },
  clickCell: {
    cursor: 'pointer',
  },
  componentCell: {
    fontWeight: 'bolder',
    letterSpacing: '1px',
    color: '#585858',
  },
  subComponentCell: {
    paddingLeft: '30px',
  },
  centerText: {
    textAlign: 'center',
  },
  whiteBg: {
    backgroundColor: 'rgba(256, 256, 256, 0.5)',
  },
  summaryTableNote: {
    color: 'darkGray',
    fontSize: 'x-small',
    textAlign: 'center',
    marginTop: '8px',
  },
};

const SummaryTableCell = withStyles((theme) => ({
  head: {
    padding: 8,
    textAlign: 'center',
  },
  body: {
    padding: '8px 4px',
  },
}))(TableCell);

const getMaxOfThree = (a, b, c) => {
  // Filter out NaN values
  const numbers = [a, b, c].filter((num) => !isNaN(num));

  // If all values are NaN, return NaN
  if (numbers.length === 0) {
    return NaN;
  }

  // Find the maximum value among the non-NaN numbers
  return Math.max(...numbers);
};

const displayAmountWithAsterisk = (report, amount) => {
  if (report) {
    var d1Amount = report.d1 != null && report.d1.overall != null ? report.d1.overall.score : null;
    var d2Amount = report.d2a != null ? report.d2a.scaled : null;
    var d3Amount = report.d2b != null ? report.d2b.scaled : null;
    var bestOfFirstThreeDomains = getMaxOfThree(d1Amount, d2Amount, d3Amount);
    if (amount && amount >= bestOfFirstThreeDomains) {
      return amount + ' * ';
    }
  }
  return amount;
};
const getAmount = (amount, showTenths, rawAmount) => {
  // Campus reports store officially-rounded scores; rawAmount carries the
  // pre-rounding value so tenths can be shown. District scores are already
  // fractional, so they have no rawAmount and fall back to amount.
  const value = showTenths && rawAmount != null && !isNaN(rawAmount) ? rawAmount : amount;
  if (value && !isNaN(value)) {
    return showTenths ? value.toFixed(1) : value.toFixed(0);
  }
  return null;
};
class Summary extends React.PureComponent {
  static propTypes = {
    classes: PropTypes.any.isRequired,
    report: PropTypes.object.isRequired,
    onChangeReportSection: PropTypes.func.isRequired,
    isFreeAccount: PropTypes.bool.isRequired,
    provisionsMessage: PropTypes.string,
    showTenths: PropTypes.bool,
  };

  render() {
    const { report, classes, isFreeAccount, onChangeReportSection, provisionsMessage, showTenths } = this.props;
    const summaryTable = [
      {
        component: true,
        cols: [
          'Domain I: Student Achievement',
          displayAmountWithAsterisk(report, getAmount(report.d1.overall.score, showTenths, report.d1.overall.scoreRaw)),
          report.d1.overall.letter,
        ],
      },
    ];
    if (report.d1.staar && report.d1.staar.scaled && report.d1.staar.letter) {
      summaryTable.push({
        component: false,
        key: ReportSectionKeys.d1staar,
        cols: [
          'STAAR Performance',
          getAmount(report.d1.staar.scaled, showTenths, report.d1.staar.scaledRaw),
          report.d1.staar.letter,
        ],
      });
    }

    if (report.d1.ccmr && report.d1.ccmr.raw) {
      summaryTable.push({
        component: false,
        key: ReportSectionKeys.d1ccmr,
        cols: [
          'CCMR',
          !isNaN(report.d1.ccmr.scaled) && parseFloat(report.d1.ccmr.scaled).toFixed(),
          report.d1.ccmr.letter,
        ],
        isNotClickable: report.ccmrSource == 'manual' || report.ccmrHasStudentData === false,
      });
    }
    if (report.d1.gradRate && report.d1.gradRate.scaled) {
      summaryTable.push({
        component: false,
        key: ReportSectionKeys.d1gradRate,
        cols: ['Graduation Rate', report.d1.gradRate.scaled, report.d1.gradRate.letter],
      });
    }

    summaryTable.push({
      component: true,
      cols: ['Domain II: School Progress', '', ''],
    });

    summaryTable.push({
      component: false,
      key: ReportSectionKeys.d2a,
      cols: [
        'Part A: Academic Growth',
        displayAmountWithAsterisk(
          report,
          numberStringOrDash(getAmount(report.d2a.scaled, showTenths, report.d2a.scaledRaw))
        ),
        numberStringOrDash(report.d2a.letter),
      ],
    });
    summaryTable.push({
      component: false,
      key: ReportSectionKeys.d2b,
      cols: [
        report.d2b.ecoDisDerived
          ? `Part B: Relative Performance (Eco Dis: ${report.d2b.ecoDis}% — derived, ${report.d2b.ecoDisDerived.numerator} of ${report.d2b.ecoDisDerived.denominator} students)`
          : `Part B: Relative Performance (Eco Dis: ${report.d2b.ecoDis}%)`,
        displayAmountWithAsterisk(
          report,
          !isNaN(report.d2b.scaled) && parseFloat(getAmount(report.d2b.scaled, showTenths, report.d2b.scaledRaw))
        ),
        report.d2b.letter,
      ],
    });
    summaryTable.push({
      component: true,
      key: ReportSectionKeys.d3,
      cols: [
        'Domain III: Closing the Gaps',
        getAmount(report.d3.scaled, showTenths, report.d3.scaledRaw),
        report.d3.letter,
      ],
    });

    /*summaryTable.push({
      component: true,
      key: ReportSectionKeys.campusesWeights,
      cols: ['Campuses Weights', '', ''],
    });
    */

    const data = summaryTable;
    const numCols = data.length > 0 ? data[0].cols.length : 3;

    const getHeaderLabel = (index) => {
      switch (index) {
        case 0:
          return '';
        case 1:
          return 'Scaled\nScore';
        case 2:
          return 'Letter\nGrade';
        case 3:
          return 'Proportion of\nOverall Rating';
        default:
          return `Column ${index + 1}`;
      }
    };

    const getRatingBackgroundColor = (rating) => {
      if (!rating) return '#757575'; // Gray for empty or undefined ratings
      switch (rating.toString().toUpperCase()) {
        case 'A':
          return isDemo ? '#57a35c' : '#4CAF50'; // Green
        case 'B':
          return isDemo ? '#4288c4' : '#2196F3'; // Blue
        case 'C':
          return isDemo ? '#9c3f93' : '#9C118B'; // Purple - exact from HTML
        case 'D':
          return isDemo ? '#d18549' : '#E0803D'; // Orange - exact from HTML
        case 'F':
          return isDemo ? '#d05a51' : '#F44336'; // Red
        default:
          return '#757575'; // Gray
      }
    };

    const handleTitleClicked = (row) => {
      const isClickable = !isFreeAccount && row.key;
      if (isClickable && !row.isNotClickable && !report.isDistrict && onChangeReportSection) {
        onChangeReportSection(row.key);
      }
    };

    const tableStyle = {
      borderCollapse: 'collapse',
      width: 'auto',
      border: isDemo ? '1px solid #ECECEC' : '2px solid #000',
    };

    const headerStyle = {
      backgroundColor: isDemo ? '#E6FBFE' : '#5DADE2', // Sky blue header
      color: 'Black',
      fontWeight: 'bold',
      textAlign: 'center',
      padding: '8px',
      border: isDemo ? '1px solid #ECECEC' : '1px solid #4A9FD9',
      fontSize: '11pt',
    };

    const getRowStyle = (row, rowIndex) => {
      // Determine if this should be a "main" row (gray background) or "sub" row (white)
      const isMainRow =
        row.component ||
        row.rowType === 'main' ||
        (row.rowType === undefined && (rowIndex === 0 || rowIndex === 1 || rowIndex === 2 || rowIndex === 5));

      return {
        backgroundColor: isMainRow ? (isDemo ? '#F5F3EF' : '#E7E3DB') : 'transparent', // Beige/gray for main rows
      };
    };

    const getCellStyle = (row, cellIndex, rowIndex) => {
      const isMainRow =
        row.component ||
        row.rowType === 'main' ||
        (row.rowType === undefined && (rowIndex === 0 || rowIndex === 1 || rowIndex === 2 || rowIndex === 5));

      const baseStyle = {
        padding: '8px',
        border: isDemo ? '1px solid #ECECEC' : '1px solid #ccc',
        fontWeight: 'bold',
      };

      if (cellIndex === 0) {
        // First column - left aligned
        return {
          ...baseStyle,
          textAlign: 'left',
          fontSize: row.component ? '12pt' : '10pt',
          backgroundColor: isMainRow ? (isDemo ? '#F5F3EF' : '#E7E3DB') : 'transparent',
          height: isDemo ? 'auto' : row.component ? '0.4in' : 'auto',
        };
      } else if (cellIndex === 2) {
        // Rating column - special background color
        const cell = row.cols[cellIndex];
        return {
          ...baseStyle,
          textAlign: 'center',
          backgroundColor: cell
            ? getRatingBackgroundColor(cell)
            : isMainRow
            ? isDemo
              ? '#F5F3EF'
              : '#E7E3DB'
            : 'transparent',
          color: cell ? '#FFFFFF' : 'black',
          fontSize: row.component ? '12pt' : '10pt',
          height: isDemo ? 'auto' : row.component ? '0.4in' : 'auto',
        };
      } else if (cellIndex === 3) {
        // Last column - special handling for empty cells
        const cell = row.cols[cellIndex];
        const isEmpty = !cell || cell.trim() === '';
        return {
          ...baseStyle,
          textAlign: 'center',
          backgroundColor:
            isEmpty && !isMainRow ? 'rgba(0,0,0,0.07)' : isMainRow ? (isDemo ? '#F5F3EF' : '#E7E3DB') : 'transparent',
          fontSize: row.component ? '12pt' : '10pt',
          height: isDemo ? 'auto' : row.component ? '0.4in' : 'auto',
        };
      } else {
        // Other columns - center aligned
        return {
          ...baseStyle,
          textAlign: 'center',
          backgroundColor: isMainRow ? (isDemo ? '#F5F3EF' : '#E7E3DB') : 'transparent',
          fontSize: row.component ? '12pt' : '10pt',
          height: isDemo ? 'auto' : row.component ? '0.4in' : 'auto',
        };
      }
    };

    return (
      <div id="reportSummary" className={classes.root}>
        <div
          style={{
            display: 'flex',
            width: '100%',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <table style={tableStyle} cellSpacing="0" cellPadding="3" border="2">
            <colgroup>
              {Array.from({ length: numCols }, (_, index) => (
                <col key={index} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {Array.from({ length: numCols }, (_, index) => (
                  <th key={index} style={headerStyle} scope="col">
                    {getHeaderLabel(index)
                      .split('\n')
                      .map((line, i) => (
                        <React.Fragment key={i}>
                          {line}
                          {i < getHeaderLabel(index).split('\n').length - 1 && <br />}
                        </React.Fragment>
                      ))}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, rowIndex) => {
                const isClickable = !isFreeAccount && row.key && !row.isNotClickable && !report.isDistrict;

                return (
                  <tr key={rowIndex} style={getRowStyle(row, rowIndex)}>
                    {row.cols.map((cell, cellIndex) => {
                      const isFirstColumn = cellIndex === 0;

                      return (
                        <td
                          key={cellIndex}
                          style={getCellStyle(row, cellIndex, rowIndex)}
                          onClick={isFirstColumn ? () => handleTitleClicked(row) : undefined}
                          onMouseEnter={
                            isFirstColumn && isClickable
                              ? (e) => {
                                  e.target.style.color = '#1976d2';
                                  e.target.style.cursor = 'pointer';
                                }
                              : undefined
                          }
                          onMouseLeave={
                            isFirstColumn && isClickable
                              ? (e) => {
                                  e.target.style.color = 'black';
                                  e.target.style.cursor = 'default';
                                }
                              : undefined
                          }
                        >
                          {cell || (cellIndex === 3 ? ' ' : '')}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Typography className={classes.summaryTableNote}>
            * indicates the domain that comprises 70% of the overall score
          </Typography>
          {provisionsMessage && provisionsMessage != 'No accountability provisions needed.' && (
            <Typography className={classNames(classes.cell, classes.centerText)}>{provisionsMessage}</Typography>
          )}
        </div>
      </div>
    );
  }
}

export default withStyles(styles)(Summary);
